import React, { useState, useRef, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Typography,
  Space,
} from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import StrikeTeamDetailsModel from '../../models/StrikeTeamDetailsModel';
import {
  FormItemLayout, TailFormItemLayout,
} from '../../utils/Constants';

import {
  tailFormItemLayout,
  TeamEditNameFormItem,
  TeamEditDescriptionFormItem,
} from './EditTeamForm';

const { Text } = Typography;

// Minimal rich text editor using browser-native contentEditable — no external library.
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value || '';
      isInitialized.current = true;
    }
  }, []); // eslint-disable-line

  const exec = (command, val) => {
    editorRef.current.focus();
    document.execCommand(command, false, val || null);
    onChange(editorRef.current.innerHTML);
  };

  const toolbarBtn = (icon, command, val) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); exec(command, val); }}
      style={{
        background: 'none', border: '1px solid #d9d9d9', borderRadius: 4,
        padding: '2px 8px', cursor: 'pointer', color: '#333', lineHeight: 1,
      }}
    >
      {icon}
    </button>
  );

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ background: '#fafafa', borderBottom: '1px solid #d9d9d9', padding: '4px 8px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {toolbarBtn(<BoldOutlined />, 'bold')}
        {toolbarBtn(<ItalicOutlined />, 'italic')}
        {toolbarBtn(<UnderlineOutlined />, 'underline')}
        {toolbarBtn(<OrderedListOutlined />, 'insertOrderedList')}
        {toolbarBtn(<UnorderedListOutlined />, 'insertUnorderedList')}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current.innerHTML)}
        data-placeholder={placeholder}
        style={{
          minHeight: 120, padding: '8px 12px', outline: 'none',
          fontSize: 14, lineHeight: 1.6, color: '#333',
        }}
      />
    </div>
  );
};

export const EditTeamNameSettingsForm = ({ onUpdated, team, teamId, ...rest }) => {
  const [form] = Form.useForm();
  const [tippingPolicy, setTippingPolicy] = useState(team?.tippingPolicy || '');

  const onFinish = (values) => {
    const capturedValues = { ...values };
    if (capturedValues.teamDescription !== undefined) {
      capturedValues.teamDescription = capturedValues.teamDescription?.trim() || null;
    }
    capturedValues.tippingPolicy = tippingPolicy || null;
    if (onUpdated) {
      onUpdated(capturedValues);
    }
  };

  return (
    <Form
      {...FormItemLayout}
      form={form}
      name="teamNameSettingsForm"
      onFinish={onFinish}
      initialValues={{ ...team }}
      scrollToFirstError
    >
      <Input.Group style={{ textAlign: 'left' }}>
        <TeamEditNameFormItem team={team}/>
        <TeamEditDescriptionFormItem team={team} />
      </Input.Group>

      <Form.Item
        label="Tipping Policy"
        tooltip="Shown to staff and customers alongside your tipping QR code. Describe how tips are distributed across your team."
      >
        <RichTextEditor
          value={tippingPolicy}
          onChange={setTippingPolicy}
          placeholder="e.g. All tips are shared equally among the team at the end of each week."
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Visible to staff and customers. Shown with your tipping QR code and included in PDF exports.
        </Text>
      </Form.Item>

      <Form.Item {...tailFormItemLayout}>
        <Button
          type="primary"
          htmlType="submit"
          style={{ width: 'auto', maxWidth: '325px' }}
        >
          Save Changes
        </Button>
      </Form.Item>
    </Form>
  );
};

export const EditTeamNameSettingsUpdate = ({ teamId, team, onLoading }) => {
    const teamDetailsModel = new StrikeTeamDetailsModel({
        teamId,
    });
    const [loading, setLoading] = useState(false);

    const handleUpdate = async ({ teamName, teamDescription, tippingPolicy }) => {
        onLoading && onLoading({ message: `Saving name ...` });
        setLoading(true);
        await teamDetailsModel.updateTeamName({ teamName, teamDescription, tippingPolicy });
        setLoading(false);
        onLoading && onLoading({ message: null });
    };

    return (
        <div style={{ marginTop: '12px', marginLeft: '12px' }}>
          <EditTeamNameSettingsForm
            team={team}
            onUpdated={handleUpdate}
            teamId={teamId}
          />
        </div>
    );
};
